/**
 * `<WorkoutRuntime />`: what runs beside an active workout whatever screen is
 * showing — the rest alarm and its buzz, the screen kept on, and the swap of
 * the readiness preview for the server's cut once the start has synced.
 *
 * Mount it ONCE, inside `DataProvider`, somewhere that stays mounted while the
 * person minimises the workout to Today (the shell chrome, T48). Mounted only
 * in the workout screen, minimising would stop the buzz and the keep-awake,
 * though the scheduled notification itself would still fire.
 *
 * It renders nothing. The exact-alarm notice it detects is read with
 * `useExactAlarmNotice()` by whichever screen shows it (T54).
 */

import type { AuthorizedFetcher } from "@repo/client/data/core";
import { fetchSessionSets, startSession } from "@repo/client/data/workout";
import {
  "workout-logging.action_complete" as actionComplete,
  "workout-logging.action_skip" as actionSkip,
  "offline.rest_body" as restBody,
  "offline.rest_channel" as restChannel,
  "native-release.rest_live_body" as restLiveBody,
  "native-release.rest_live_channel" as restLiveChannel,
  "native-release.rest_live_title" as restLiveTitle,
  "offline.rest_title" as restTitle,
} from "@repo/i18n";
import { onlineManager } from "@tanstack/react-query";
import { useContext, useEffect, useSyncExternalStore } from "react";
import { useApi } from "../../../data/api";
import { OutboxContext, type OutboxRunner } from "../../../data/outbox-runner";
import {
  allowSleep,
  cancelNotification,
  exactAlarmState,
  hideRestCountdown,
  impact,
  keepAwake,
  onLocalNotificationAction,
  onRestTimerAction,
  preferences,
  REST_ACTION_COMPLETE,
  REST_ACTION_SKIP,
  requestExactAlarms,
  restTimerAvailable,
  scheduleNotification,
  showRestCountdown,
} from "../../../native";
import { workoutPrefsStore } from "../prefs";
import { controlsOf, measuresOf } from "../screens/next-set";
import {
  isStartReplayDue,
  replayStartForAdjustments,
} from "./adjustments-sync";
import { workoutActionSettings, workoutStore } from "./instance";
import { createWorkoutOutbox, outboxPortOf } from "./outbox-bridge";
import {
  createRestAlert,
  markExactAlarmsNoticed,
  REST_NOTIFICATION_ID,
  shouldNoticeExactAlarms,
} from "./rest-alert";
import { playRestSound } from "./rest-sound";
import { endRest, liveOf, settleAdjustments } from "./session-model";
import { isSetsReconcileDue, reconcileSessionSets } from "./sets-reconcile";
import { createWorkoutActions, systemClock } from "./workout-store";

// ── The exact-alarm notice, as a tiny external store ─────────────────────────

let noticeVisible = false;
const noticeListeners = new Set<() => void>();
const setNotice = (visible: boolean) => {
  if (visible !== noticeVisible) {
    noticeVisible = visible;
    for (const listener of noticeListeners) {
      listener();
    }
  }
};
const subscribeNotice = (listener: () => void) => {
  noticeListeners.add(listener);
  return () => {
    noticeListeners.delete(listener);
  };
};

const noticeStorage = {
  getItem: (key: string) => preferences.get(key),
  setItem: (key: string, value: string) => preferences.set(key, value),
};

/**
 * "The rest alarm may be late": shown once per install, the first time a rest
 * is scheduled inexact because exact alarms are off.
 */
export function useExactAlarmNotice(): {
  allow(): Promise<void>;
  dismiss(): void;
  readonly visible: boolean;
} {
  const visible = useSyncExternalStore(
    subscribeNotice,
    () => noticeVisible,
    () => false
  );
  return {
    async allow() {
      setNotice(false);
      await markExactAlarmsNoticed({ storage: noticeStorage });
      await requestExactAlarms();
    },
    dismiss() {
      setNotice(false);
      markExactAlarmsNoticed({ storage: noticeStorage }).catch(() => undefined);
    },
    visible,
  };
}

// ── The runtime ──────────────────────────────────────────────────────────────

/** `finishRest` without the outbox: the rest ending is a local move. */
function finishRestLocally(): void {
  const { session } = workoutStore.getState();
  if (
    session !== null &&
    session.restEndsAt !== null &&
    session.restEndsAt <= Date.now()
  ) {
    const next = endRest(session);
    if (next !== null) {
      workoutStore.setState({ session: next });
    }
  }
}

/**
 * The rest alarm and keep-awake follow the store, after hydration only:
 * before it, "no session" would cancel an alarm a relaunch should keep.
 * Returns the stop.
 */
function startRestAlert(): () => void {
  let checkedNotice = false;
  const alert = createRestAlert({
    allowSleep,
    cancelNotification,
    // `D283`: only where the shell carries the plugin; an older APK keeps
    // the end alarm alone, as before.
    ...(restTimerAvailable()
      ? {
          countdown: {
            hide: hideRestCountdown,
            show: (endsAt: number) =>
              showRestCountdown({
                body: restLiveBody(),
                channelName: restLiveChannel(),
                completeLabel: actionComplete(),
                endsAt,
                skipLabel: actionSkip(),
                title: restLiveTitle(),
              }),
          },
        }
      : {}),
    impact,
    isForeground: () => document.visibilityState === "visible",
    keepAwake,
    // The three `D209` settings (B8, B11), read at the moment they apply.
    keepAwakeEnabled: () => workoutPrefsStore.getState().keepAwake,
    now: () => Date.now(),
    onRestElapsed: finishRestLocally,
    playSound: () => {
      const { restSound, restVolume } = workoutPrefsStore.getState();
      playRestSound(restSound, restVolume);
    },
    onScheduled: (outcome) => {
      if (checkedNotice) {
        return;
      }
      checkedNotice = true;
      shouldNoticeExactAlarms(outcome, {
        exactAlarmState,
        storage: noticeStorage,
      })
        .then(setNotice)
        .catch(() => undefined);
    },
    scheduleNotification,
    texts: () => ({
      actions: { complete: actionComplete(), skip: actionSkip() },
      body: restBody(),
      channelName: restChannel(),
      title: restTitle(),
    }),
    vibrate: () => workoutPrefsStore.getState().vibrate,
  });
  workoutPrefsStore.load().catch(() => undefined);
  // The keep-awake setting changed: the alert re-decides with the session
  // as it stands, so turning it off lets the screen sleep at once.
  const offPrefs = workoutPrefsStore.subscribe(() => {
    if (workoutStore.hydration.getStatus().hydrated) {
      alert.update(workoutStore.getState().session);
    }
  });
  let unsubscribe: (() => void) | null = null;
  let stopped = false;
  workoutStore.hydration.whenHydrated().then(() => {
    if (stopped) {
      return;
    }
    alert.update(workoutStore.getState().session);
    unsubscribe = workoutStore.subscribe((state) => {
      alert.update(state.session);
    });
  });
  return () => {
    stopped = true;
    unsubscribe?.();
    offPrefs();
    alert.stop();
  };
}

/**
 * The rest notification's buttons (`D209`, B9). "Pular descanso" ends the
 * rest; "Concluir série" ends it if it is still running and logs the next
 * set with the numbers on the dials — the set the person just did, from the
 * shade, through the same outbox write as "Registrar série". Both wait for
 * hydration: a button that opened the app cold arrives before the session
 * is read. Returns the stop.
 */
function startRestActions(runner: OutboxRunner): () => void {
  const actions = createWorkoutActions(
    workoutStore,
    createWorkoutOutbox(outboxPortOf(runner)),
    systemClock,
    workoutActionSettings
  );
  const act = (actionId: string) => {
    workoutStore.hydration
      .whenHydrated()
      .then(() => {
        const { session } = workoutStore.getState();
        if (session === null || session.ended !== null) {
          return;
        }
        if (actionId === REST_ACTION_SKIP) {
          actions.skipRest();
          return;
        }
        if (actionId !== REST_ACTION_COMPLETE) {
          return;
        }
        actions.skipRest();
        const after = workoutStore.getState().session;
        if (after === null || after.phase !== "log") {
          return;
        }
        const current = liveOf(after).exercises[after.exIndex];
        return actions.logSet(measuresOf(after, controlsOf(current), null));
      })
      .catch((error: unknown) => {
        console.warn("[workout] rest notification action failed", error);
      });
  };
  // The end alarm's buttons and the countdown's (`D283`) say the same two
  // things, so they take the same path.
  const offAlarm = onLocalNotificationAction(REST_NOTIFICATION_ID, act);
  const offCountdown = onRestTimerAction(act);
  return () => {
    offAlarm();
    offCountdown();
  };
}

/**
 * The server's cut replaces the preview once the start has left the queue.
 * Returns the stop. Outside the component: the React Compiler cannot lower a
 * `try` without a `catch`, and it would skip the whole component silently.
 */
function startAdjustmentsSync(
  api: AuthorizedFetcher,
  runner: OutboxRunner
): () => void {
  let busy = false;
  let stopped = false;
  const check = async () => {
    if (
      busy ||
      stopped ||
      !onlineManager.isOnline() ||
      !workoutStore.hydration.getStatus().hydrated
    ) {
      return;
    }
    await runner.store.load();
    const { session } = workoutStore.getState();
    if (!isStartReplayDue(session, runner.store.getState())) {
      return;
    }
    busy = true;
    try {
      await replayStartForAdjustments(session, {
        replayStart: (body) => startSession(api, body),
        settle: (sessionId, adjustments) => {
          const { session: current } = workoutStore.getState();
          const next =
            current === null
              ? null
              : settleAdjustments(current, sessionId, adjustments);
          if (next !== null) {
            workoutStore.setState({ session: next });
          }
        },
      });
    } finally {
      busy = false;
    }
  };
  const run = () => {
    check().catch((error: unknown) => {
      console.warn("[workout] adjustments sync failed", error);
    });
  };
  const offOutbox = runner.store.subscribe(run);
  const offHydration = workoutStore.hydration.subscribe(run);
  // A failed replay (a dead connection) is tried again on the next change:
  // the network coming back, or the session moving.
  const offOnline = onlineManager.subscribe((online) => {
    if (online) {
      run();
    }
  });
  const offSession = workoutStore.subscribe(run);
  run();
  return () => {
    stopped = true;
    offOutbox();
    offHydration();
    offOnline();
    offSession();
  };
}

/**
 * The resumed session against the server's sets, once its queue is empty
 * (`sets-reconcile.ts`, T-188). Returns the stop. Outside the component for
 * the same reason as {@link startAdjustmentsSync}.
 */
function startSetsReconcile(
  api: AuthorizedFetcher,
  runner: OutboxRunner
): () => void {
  const actions = createWorkoutActions(
    workoutStore,
    createWorkoutOutbox(outboxPortOf(runner))
  );
  const done = new Set<string>();
  let busy = false;
  let stopped = false;
  const check = async () => {
    if (
      busy ||
      stopped ||
      !onlineManager.isOnline() ||
      !workoutStore.hydration.getStatus().hydrated
    ) {
      return;
    }
    await runner.store.load();
    const { session } = workoutStore.getState();
    if (!isSetsReconcileDue(session, runner.store.getState(), done)) {
      return;
    }
    busy = true;
    try {
      const outcome = await reconcileSessionSets(session, {
        fetchSets: (sessionId) => fetchSessionSets(api, sessionId),
        isStillDue: (asked) => {
          const { session: current } = workoutStore.getState();
          return (
            !stopped &&
            current?.logged === asked.logged &&
            isSetsReconcileDue(current, runner.store.getState(), done)
          );
        },
        reconcile: (sessionId, sets) => actions.reconcileSets(sessionId, sets),
      });
      if (outcome === "done") {
        done.add(session.sessionId);
      }
    } finally {
      busy = false;
    }
  };
  const run = () => {
    check().catch((error: unknown) => {
      console.warn("[workout] sets reconcile failed", error);
    });
  };
  const offOutbox = runner.store.subscribe(run);
  const offHydration = workoutStore.hydration.subscribe(run);
  const offOnline = onlineManager.subscribe((online) => {
    if (online) {
      run();
    }
  });
  const offSession = workoutStore.subscribe(run);
  run();
  return () => {
    stopped = true;
    offOutbox();
    offHydration();
    offOnline();
    offSession();
  };
}

export function WorkoutRuntime(): null {
  const api = useApi();
  const runner = useContext(OutboxContext);
  useEffect(() => startRestAlert(), []);
  useEffect(
    () => (runner === null ? undefined : startAdjustmentsSync(api, runner)),
    [api, runner]
  );
  useEffect(
    () => (runner === null ? undefined : startSetsReconcile(api, runner)),
    [api, runner]
  );
  useEffect(
    () => (runner === null ? undefined : startRestActions(runner)),
    [runner]
  );
  return null;
}
